import React from 'react';
import { motion } from 'framer-motion';
import { Check, CircleNotch, Lightning, MagnifyingGlass, Globe, Link, Brain, CheckCircle, Database, Camera, Cursor, TextAa } from '@phosphor-icons/react';

const STEP_ICONS = {
  search: MagnifyingGlass,
  browse: Globe,
  links: Link,
  think: Brain,
  done: CheckCircle,
  data: Database,
  screenshot: Camera,
  form: TextAa,
  click: Cursor,
};

export default function AgentSteps({ steps }) {
  if (!steps || !steps.length) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-4 rounded-xl border border-lime-400/25 bg-lime-400/10 overflow-hidden"
      data-testid="agent-steps-container">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-lime-400/20 bg-lime-400/10">
        <Lightning weight="fill" className="w-3.5 h-3.5 text-lime-400" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-lime-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          Agent — Working
        </span>
        {steps.some(s => s.status === 'active') && (
          <div className="ml-auto flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-lime-400/100 animate-pulse" />
            <span className="text-[10px] text-lime-400 font-mono">Live</span>
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="p-3 space-y-1">
        {steps.map((s, i) => {
          const StepIcon = STEP_ICONS[s.type] || Lightning;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05, duration: 0.25 }}
              className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
                s.status === 'active' ? 'bg-white/5 border border-lime-400/25 shadow-sm' : ''
              }`}
              data-testid={`agent-step-${i}`}
            >
              {/* Icon */}
              <div className="mt-0.5 shrink-0">
                {s.status === 'completed' ? (
                  <div className="w-5 h-5 rounded-md bg-lime-400/15 flex items-center justify-center">
                    <Check weight="bold" className="w-3 h-3 text-lime-400" />
                  </div>
                ) : s.status === 'active' ? (
                  <div className="w-5 h-5 rounded-md bg-lime-400/100 flex items-center justify-center">
                    <CircleNotch weight="bold" className="w-3 h-3 text-white animate-spin" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-md bg-white/10 flex items-center justify-center">
                    <StepIcon className="w-3 h-3 text-white/40" />
                  </div>
                )}
              </div>

              {/* Label */}
              <div className="flex-1 min-w-0">
                <p className={`text-[12px] leading-relaxed ${
                  s.status === 'completed' ? 'text-white/70' : s.status === 'active' ? 'text-white/90 font-medium' : 'text-white/40'
                }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {s.step}
                </p>
                {s.url && s.status === 'active' && (
                  <p className="text-[10px] text-lime-400 truncate mt-0.5">{s.url}</p>
                )}
                {s.query && s.status === 'active' && (
                  <p className="text-[10px] text-lime-400 mt-0.5">Query: {s.query}</p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
